import { useState, useMemo } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Badge,
  Button,
  Checkbox,
  Card,
  CardBody,
  EmptyState as DSEmptyState,
  Skeleton,
} from '@kollektive/design-system';
import { Trash2, Key, AlertTriangle, Search, Copy, Check, Loader2 } from 'lucide-react';
import type { UserToken, UserRole } from '@/types';
import styles from './TokenTable.module.css';

interface TokenTableProps {
  tokens: UserToken[];
  currentUserId: string;
  currentUserRole: UserRole;
  isLoading: boolean;
  error: Error | null;
  onRetry?: () => void;
  onRevoke: (token: UserToken) => void;
  onBulkRevoke: (tokens: UserToken[]) => void;
  onRevealToken: (tokenId: string) => Promise<string>;
  isRevoking?: boolean;
  searchQuery?: string;
}

// Helper to determine if the current user can manage a specific token
function canManageToken(
  currentUserId: string,
  currentUserRole: UserRole,
  token: UserToken
): boolean {
  // Users can always manage their own tokens
  if (token.user_id === currentUserId) return true;

  // Owners can manage any token
  if (currentUserRole === 'owner') return true;

  // Admins can manage non-owner tokens
  if (currentUserRole === 'admin' && token.user_role !== 'owner') return true;

  // Members and viewers can only manage their own tokens
  return false;
}

type SortField = 'name' | 'user_name' | 'created_at' | 'expires_at' | 'last_used_at';
type SortDirection = 'asc' | 'desc';

// Helper functions
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return formatDate(dateString);
}

type ExpiryStatus = 'active' | 'expires-soon' | 'expired';
type BadgeVariant = 'success' | 'warning' | 'error';

function getExpiryStatus(expiresAt: string | null): ExpiryStatus {
  if (!expiresAt) return 'active';

  const expiry = new Date(expiresAt);
  const now = new Date();
  const daysUntilExpiry = Math.floor((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (daysUntilExpiry < 0) return 'expired';
  if (daysUntilExpiry <= 7) return 'expires-soon';
  return 'active';
}

function getExpiryLabel(expiresAt: string | null): string {
  if (!expiresAt) return 'Never';

  const status = getExpiryStatus(expiresAt);
  const expiry = new Date(expiresAt);
  const now = new Date();
  const daysUntilExpiry = Math.floor((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (status === 'expired') return 'Expired';
  if (daysUntilExpiry === 0) return 'Today';
  if (daysUntilExpiry === 1) return 'Tomorrow';
  if (daysUntilExpiry <= 7) return `${daysUntilExpiry} days`;
  return formatDate(expiresAt);
}

const statusToBadgeVariant: Record<ExpiryStatus, BadgeVariant> = {
  active: 'success',
  'expires-soon': 'warning',
  expired: 'error',
};

// Skeleton component for loading state
function TableSkeleton() {
  return (
    <Card>
      <CardBody className={styles.cardBody}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader style={{ width: 40 }}><Skeleton width={16} height={16} /></TableHeader>
              <TableHeader>Name</TableHeader>
              <TableHeader>Token</TableHeader>
              <TableHeader>User</TableHeader>
              <TableHeader>Created</TableHeader>
              <TableHeader>Expires</TableHeader>
              <TableHeader>Last Used</TableHeader>
              <TableHeader style={{ width: 60 }}></TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2, 3].map((i) => (
              <TableRow key={i}>
                <TableCell><Skeleton width={16} height={16} /></TableCell>
                <TableCell>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Skeleton width="6rem" height="1rem" />
                    <Skeleton width="3rem" height="0.75rem" />
                  </div>
                </TableCell>
                <TableCell>
                  <Skeleton width="7rem" height="1.5rem" />
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Skeleton width="5rem" height="1rem" />
                    <Skeleton width="8rem" height="0.75rem" />
                  </div>
                </TableCell>
                <TableCell><Skeleton width="5rem" height="1rem" /></TableCell>
                <TableCell><Skeleton width="4rem" height="1.25rem" /></TableCell>
                <TableCell><Skeleton width="4rem" height="1rem" /></TableCell>
                <TableCell><Skeleton width={24} height={24} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardBody>
    </Card>
  );
}

export function TokenTable({
  tokens,
  currentUserId,
  currentUserRole,
  isLoading,
  error,
  onRetry,
  onRevoke,
  onBulkRevoke,
  onRevealToken,
  isRevoking,
  searchQuery = '',
}: TokenTableProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [copiedTokenId, setCopiedTokenId] = useState<string | null>(null);
  const [copyingTokenId, setCopyingTokenId] = useState<string | null>(null);

  const handleCopyToken = async (tokenId: string) => {
    if (copyingTokenId) return; // Prevent multiple simultaneous requests

    setCopyingTokenId(tokenId);
    let revealedToken: string | null = null;

    try {
      // Fetch the decrypted token from the API
      revealedToken = await onRevealToken(tokenId);

      // Copy to clipboard
      await navigator.clipboard.writeText(revealedToken);

      setCopiedTokenId(tokenId);
      setTimeout(() => setCopiedTokenId(null), 2000);
    } catch (err) {
      console.error('Failed to copy token:', err);
    } finally {
      // Clear the revealed token from memory
      revealedToken = null;
      setCopyingTokenId(null);
    }
  };

  // Filter tokens by search query
  const filteredTokens = useMemo(() => {
    if (!searchQuery.trim()) return tokens;

    const query = searchQuery.toLowerCase();
    return tokens.filter((token) =>
      token.name?.toLowerCase().includes(query) ||
      token.user_name?.toLowerCase().includes(query) ||
      token.user_email?.toLowerCase().includes(query) ||
      token.role.toLowerCase().includes(query) ||
      token.permissions.some((p) => p.toLowerCase().includes(query))
    );
  }, [tokens, searchQuery]);

  // Sort filtered tokens
  const sortedTokens = useMemo(() => {
    return [...filteredTokens].sort((a, b) => {
      let aVal: string | number | null;
      let bVal: string | number | null;

      switch (sortField) {
        case 'name':
          aVal = a.name || '';
          bVal = b.name || '';
          break;
        case 'user_name':
          aVal = a.user_name || '';
          bVal = b.user_name || '';
          break;
        case 'created_at':
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
        case 'expires_at':
          aVal = a.expires_at ? new Date(a.expires_at).getTime() : Number.MAX_SAFE_INTEGER;
          bVal = b.expires_at ? new Date(b.expires_at).getTime() : Number.MAX_SAFE_INTEGER;
          break;
        case 'last_used_at':
          aVal = a.last_used_at ? new Date(a.last_used_at).getTime() : 0;
          bVal = b.last_used_at ? new Date(b.last_used_at).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortDirection === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });
  }, [filteredTokens, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get tokens that the current user can manage
  const manageableTokens = sortedTokens.filter((t) =>
    canManageToken(currentUserId, currentUserRole, t)
  );

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Only select tokens the user can manage
      setSelectedIds(new Set(manageableTokens.map((t) => t.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (tokenId: string, checked: boolean) => {
    const newSet = new Set(selectedIds);
    if (checked) {
      newSet.add(tokenId);
    } else {
      newSet.delete(tokenId);
    }
    setSelectedIds(newSet);
  };

  const handleBulkRevoke = () => {
    const tokensToRevoke = tokens.filter((t) => selectedIds.has(t.id));
    onBulkRevoke(tokensToRevoke);
    setSelectedIds(new Set());
  };

  const allSelected = manageableTokens.length > 0 && selectedIds.size === manageableTokens.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < manageableTokens.length;

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (error) {
    return (
      <DSEmptyState
        variant="error"
        icon={<AlertTriangle size={48} />}
        title="Failed to load tokens"
        description={error.message}
        action={onRetry ? <Button onClick={onRetry}>Try again</Button> : undefined}
      />
    );
  }

  if (tokens.length === 0) {
    return (
      <DSEmptyState
        icon={<Key size={48} />}
        title="No tokens yet"
        description="Create an MCP access token to connect your AI tools to your databases."
      />
    );
  }

  return (
    <div className={styles.container}>
      {/* Table in Card */}
      {filteredTokens.length > 0 ? (
        <Card className={styles.tableCard}>
          <CardBody className={styles.cardBody}>
            <Table hoverable>
              <TableHead>
                <TableRow>
                  <TableHeader style={{ width: 40 }} className={styles.checkboxCell}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </TableHeader>
                  <TableHeader
                    sortable
                    sortDirection={sortField === 'name' ? sortDirection : null}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableHeader>
                  <TableHeader>
                    Token
                  </TableHeader>
                  <TableHeader
                    sortable
                    sortDirection={sortField === 'user_name' ? sortDirection : null}
                    onClick={() => handleSort('user_name')}
                  >
                    User
                  </TableHeader>
                  <TableHeader
                    sortable
                    sortDirection={sortField === 'created_at' ? sortDirection : null}
                    onClick={() => handleSort('created_at')}
                  >
                    Created
                  </TableHeader>
                  <TableHeader
                    sortable
                    sortDirection={sortField === 'expires_at' ? sortDirection : null}
                    onClick={() => handleSort('expires_at')}
                  >
                    Expires
                  </TableHeader>
                  <TableHeader
                    sortable
                    sortDirection={sortField === 'last_used_at' ? sortDirection : null}
                    onClick={() => handleSort('last_used_at')}
                  >
                    Last Used
                  </TableHeader>
                  <TableHeader style={{ width: 60 }}></TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedTokens.map((token) => {
                  const expiryStatus = getExpiryStatus(token.expires_at);
                  const expiryLabel = getExpiryLabel(token.expires_at);
                  const badgeVariant = statusToBadgeVariant[expiryStatus];
                  const tokenName = token.name || 'Unnamed token';

                  return (
                    <TableRow key={token.id} selected={selectedIds.has(token.id)}>
                      <TableCell className={styles.checkboxCell}>
                        <Checkbox
                          checked={selectedIds.has(token.id)}
                          onChange={(e) => handleSelectOne(token.id, e.target.checked)}
                          disabled={!canManageToken(currentUserId, currentUserRole, token)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className={styles.nameCell}>
                          <span className={styles.tokenName}>{tokenName}</span>
                          <span className={styles.tokenRole}>{token.role}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {token.preview ? (
                          <div className={styles.tokenPreviewCell}>
                            <code className={styles.tokenPreview}>{token.preview}</code>
                            <button
                              type="button"
                              className={styles.copyButton}
                              onClick={() => handleCopyToken(token.id)}
                              disabled={copyingTokenId === token.id}
                              title="Copy full token to clipboard"
                            >
                              {copyingTokenId === token.id ? (
                                <Loader2 size={14} className={styles.copyIconLoading} />
                              ) : copiedTokenId === token.id ? (
                                <Check size={14} className={styles.copyIconSuccess} />
                              ) : (
                                <Copy size={14} />
                              )}
                            </button>
                          </div>
                        ) : (
                          <span className={styles.noPreview}>—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className={styles.userCell}>
                          <span className={styles.userName}>{token.user_name || 'Unknown'}</span>
                          <span className={styles.userEmail}>{token.user_email || ''}</span>
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(token.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant={badgeVariant} styleVariant="subtle" size="sm">
                          {expiryLabel}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className={styles.lastUsed}>
                          {formatRelativeTime(token.last_used_at)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {canManageToken(currentUserId, currentUserRole, token) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onRevoke(token)}
                            disabled={isRevoking}
                            title="Revoke token"
                            className={styles.revokeButton}
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardBody>
        </Card>
      ) : (
        <Card className={styles.tableCard}>
          <CardBody>
            <DSEmptyState
              variant="search"
              icon={<Search size={48} />}
              title="No results"
              description={`No tokens match "${searchQuery}"`}
            />
          </CardBody>
        </Card>
      )}

      {/* Bulk actions bar at bottom - always rendered to prevent layout shift */}
      <div className={`${styles.bulkActions} ${selectedIds.size === 0 ? styles.bulkActionsHidden : ''}`}>
        <span className={styles.selectedCount}>
          {selectedIds.size} selected
        </span>
        <Button
          variant="danger"
          size="sm"
          onClick={handleBulkRevoke}
          disabled={isRevoking}
          iconLeft={<Trash2 size={16} />}
        >
          Revoke Selected
        </Button>
      </div>
    </div>
  );
}
